import "@refinedev/antd/dist/reset.css";
import "./global.css";

import { queryClient } from "@admin/utils/queryClient";
import { Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import routerBindings, { UnsavedChangesNotifier } from "@refinedev/react-router-v6";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConfigProvider } from "antd";
import { toSafeInteger } from "lodash-es";
import type { FC, PropsWithChildren } from "react";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertProvider,DocumentTitleHandler } from "ui";
import { ErrorBoundary } from "ui/error";
import { WebworkerProvider } from "utils/contexts";
import { updateDayjsLocale } from "utils/date";
import { useMessage, useNetworkTimeout } from "utils/hooks";

import { authProvider } from "@/authProvider";

import { accessControlProvider } from "./accessControlProvider";
import { APP_RESOURCE } from "./app.constant";
import dataProvider from "./dataProvider";
import { en, ja,zh } from "./locale";
import { notificationProvider } from "./notificationProvider";
import { formConfig, theme } from "./themeConfig";

const TIMEOUT = import.meta.env.VITE_NETWORK_TIMEOUT;

export const AppWrapper: FC<PropsWithChildren> = ({ children }) => {
  const { t, i18n } = useTranslation(["common", "property", "device", "option", "button", "form"]);
  const { contextHolder, message } = useMessage();

  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };
  const antdLocale = useMemo(() => {
    updateDayjsLocale(i18n.language);
    switch (i18n.language) {
      case "en":
        return en;
      case "zh":
        return zh;
      case "jp":
        return ja;
      default:
        return ja;
    }
  }, [i18n.language]);

  return (
    <QueryClientProvider client={queryClient}>
      <WebworkerProvider>
        <ConfigProvider form={formConfig} locale={antdLocale} theme={theme}>
          <ErrorBoundary>
            <RefineKbarProvider>
              <Refine
                accessControlProvider={accessControlProvider}
                authProvider={authProvider}
                dataProvider={dataProvider}
                i18nProvider={i18nProvider}
                notificationProvider={notificationProvider(message)}
                options={{
                  breadcrumb: false,
                  syncWithLocation: false,
                  warnWhenUnsavedChanges: true,
                  projectId: "A69idI-86AY7i-77NzCo",
                  reactQuery: { clientConfig: queryClient },
                }}
                resources={APP_RESOURCE}
                routerProvider={routerBindings}
              >
                <AlertProvider>
                  {contextHolder}
                  <Inside>{children}</Inside>
                  <RefineKbar />
                  <UnsavedChangesNotifier />
                  <DocumentTitleHandler title="Pabbit Admin" />
                  <ReactQueryDevtools />
                </AlertProvider>
              </Refine>
            </RefineKbarProvider>
          </ErrorBoundary>
        </ConfigProvider>
      </WebworkerProvider>
    </QueryClientProvider>
  );
};
const Inside: FC<PropsWithChildren> = ({ children }) => {
  useNetworkTimeout(toSafeInteger(TIMEOUT) || undefined);
  return <Fragment>{children}</Fragment>;
};

export default AppWrapper;
